import { useMemo } from 'react';
import { useGetSubscription } from '@/api/queries/useSubscriptionQueries';
import {
  SubscriptionTier,
  type FeatureName,
  type Entitlements,
  type SubscriptionStatus,
  type ArchiveAccess,
  type IssueCadence,
} from '@/types/subscription';

/**
 * Statuses that count as "in good standing" for a subscription, mirroring
 * the backend's `Subscription.is_active` property
 * (paire_api/models/subscriptions.py) — includes the 'trialing' and
 * 'past_due' grace periods, not just 'active'.
 */
const ACTIVE_SUBSCRIPTION_STATUSES: readonly SubscriptionStatus[] = [
  'active',
  'trialing',
  'past_due',
];

/**
 * Feature access result.
 */
export interface FeatureAccessResult {
  /** Whether the user has access to the feature */
  hasAccess: boolean;
  /** Required tier to access the feature */
  requiredTier: SubscriptionTier;
  /** Whether a limit has been reached (for countable features) */
  limitReached?: boolean;
  /** Current count (for countable features) */
  currentCount?: number;
  /** Limit value (for countable features) */
  limit?: number | null;
  /** Remaining count until limit (for countable features) */
  remaining?: number | null;
  /** Whether the subscription data is loading */
  isLoading: boolean;
  /** User's current tier */
  currentTier: SubscriptionTier;
  /** All entitlements */
  entitlements: Entitlements | null;
}

/**
 * Default entitlements for when subscription hasn't loaded.
 */
const DEFAULT_ENTITLEMENTS: Entitlements = {
  tasteMatching: true,
  personalTasteMatching: true,
  personalListLimit: 3,
  groupListLimit: 1,
  groupMemberLimit: 4,
  groupRestaurantLimit: 10,
  simpleVoting: true,
  survivorVoting: false,
  rankingVoting: false,
  tasteInsightsGeneration: false,
  tasteInsightsViewing: true,
  archiveAccess: 'current_only',
  issueCadence: 'weekly',
};

/**
 * Map feature names to entitlement keys.
 */
function getEntitlementValue(
  entitlements: Entitlements,
  feature: FeatureName
): boolean | number | null {
  switch (feature) {
    case 'taste_matching':
      return entitlements.tasteMatching;
    case 'personal_taste_matching':
      return entitlements.personalTasteMatching;
    case 'personal_lists':
      return entitlements.personalListLimit;
    case 'group_lists':
      return entitlements.groupListLimit;
    case 'simple_voting':
      return entitlements.simpleVoting;
    case 'survivor_voting':
      return entitlements.survivorVoting;
    case 'ranking_voting':
      return entitlements.rankingVoting;
    case 'taste_insights_generation':
      return entitlements.tasteInsightsGeneration;
    case 'taste_insights_viewing':
      return entitlements.tasteInsightsViewing;
    default:
      return false;
  }
}

/**
 * Hook to check if the current user has access to a feature.
 *
 * @param feature - The feature to check access for
 * @param currentCount - Optional current count for countable features
 * @returns Feature access result
 *
 * @example
 * ```tsx
 * const { hasAccess, limitReached, remaining } = useFeatureAccess('personal_lists', myLists.length);
 * if (!hasAccess || limitReached) {
 *   // Show upgrade prompt
 * }
 * ```
 */
export function useFeatureAccess(
  feature: FeatureName,
  currentCount?: number
): FeatureAccessResult {
  const { data: subscription, isLoading } = useGetSubscription();

  return useMemo(() => {
    // Same per-key fallback as `useEntitlements()`, so the `entitlements` this
    // hook hands back never carries `undefined` for `archiveAccess`/`issueCadence`.
    const entitlements = withDigestEntitlementFallbacks(subscription?.entitlements ?? DEFAULT_ENTITLEMENTS);
    const currentTier = subscription?.tier ?? SubscriptionTier.FREE;
    const value = getEntitlementValue(entitlements, feature);

    // Boolean features (voting modes, TasteInsights, etc.)
    if (typeof value === 'boolean') {
      return {
        hasAccess: value,
        requiredTier: value ? SubscriptionTier.FREE : SubscriptionTier.PLUS,
        isLoading,
        currentTier,
        entitlements,
      };
    }

    // Numeric features (list limits)
    // null means unlimited
    if (value === null) {
      return {
        hasAccess: true,
        requiredTier: SubscriptionTier.FREE,
        limit: null,
        remaining: null,
        isLoading,
        currentTier,
        entitlements,
      };
    }

    // Limited features
    const limit = value;
    const count = currentCount ?? 0;
    const remaining = Math.max(0, limit - count);
    const limitReached = count >= limit;

    return {
      hasAccess: !limitReached,
      requiredTier: limitReached ? SubscriptionTier.PLUS : SubscriptionTier.FREE,
      limitReached,
      currentCount: count,
      limit,
      remaining,
      isLoading,
      currentTier,
      entitlements,
    };
  }, [subscription, feature, currentCount, isLoading]);
}

/**
 * Hook to check if the user has a premium subscription.
 *
 * Mirrors the backend's `SubscriptionService.is_effectively_free_tier` /
 * `Subscription.is_active` semantics (D33 Canonical Rule): a lapsed/inactive
 * subscription is treated the same as genuine Free tier for feature-gating
 * purposes, even if its tier is still Plus/Pro. Tier alone is not enough —
 * status must also be in good standing (active, trialing, or the past_due
 * grace period).
 */
export function useIsPremium(): { isPremium: boolean; isLoading: boolean } {
  const { data: subscription, isLoading } = useGetSubscription();

  return useMemo(
    () => ({
      isPremium:
        (subscription?.tier ?? SubscriptionTier.FREE) >= SubscriptionTier.PLUS &&
        (subscription
          ? ACTIVE_SUBSCRIPTION_STATUSES.includes(subscription.status)
          : false),
      isLoading,
    }),
    [subscription, isLoading]
  );
}

/**
 * `subscription?.entitlements ?? DEFAULT_ENTITLEMENTS` is a whole-object
 * coalesce — it only ever falls back when `entitlements` itself is
 * missing. A loaded response whose `entitlements` object is present but
 * whose `archiveAccess`/`issueCadence` keys are genuinely absent (the
 * pre-coordinated-release backend case D51f's Constraints call out) would
 * otherwise carry `undefined` for those two keys at runtime despite the
 * `Entitlements` type claiming they're always present. This backfills
 * just those two keys from `DEFAULT_ENTITLEMENTS`'s free-shaped fallback,
 * per-key, without disturbing any other entitlement.
 */
function withDigestEntitlementFallbacks(entitlements: Entitlements): Entitlements {
  return {
    ...entitlements,
    archiveAccess: entitlements.archiveAccess ?? DEFAULT_ENTITLEMENTS.archiveAccess,
    issueCadence: entitlements.issueCadence ?? DEFAULT_ENTITLEMENTS.issueCadence,
  };
}

/**
 * Hook to get all entitlements for the current user.
 */
export function useEntitlements(): {
  entitlements: Entitlements;
  tier: SubscriptionTier;
  isLoading: boolean;
} {
  const { data: subscription, isLoading } = useGetSubscription();

  return useMemo(
    () => ({
      entitlements: withDigestEntitlementFallbacks(subscription?.entitlements ?? DEFAULT_ENTITLEMENTS),
      tier: subscription?.tier ?? SubscriptionTier.FREE,
      isLoading,
    }),
    [subscription, isLoading]
  );
}

/**
 * D51f Phase 2 — the entitlements contract every new Digest surface
 * that decides by tier (the masthead, the closing card, the Previous
 * Issues lock state) must read `archiveAccess`/`issueCadence` through,
 * per the plan's Constraints: "never `issue.cadence` and never
 * `useIsPremium()`." (All Issues rows are not consumers: they take their
 * lock state from the server's per-item `locked` flag.)
 *
 * Three states, as a discriminated union on `status`, because there are three
 * genuinely different situations and only one of them is safe to render a
 * tier-specific branch for:
 *
 * - `'loading'` — the subscription request is pending or has not started.
 * - `'unavailable'` — the member's tier is UNKNOWN. This must never fall back
 *   to free-tier defaults: a paying member would be shown "you're on the
 *   weekly edition" and an upsell. Two distinct causes land here:
 *   - the request FAILED (`isError`) or is paused offline with nothing
 *     cached (`fetchStatus === 'paused'`);
 *   - the request succeeded but the payload predates the D51d/D51f keys
 *     entirely — `archiveAccess` AND `issueCadence` are BOTH absent (a
 *     deploy-order mismatch: the API hasn't shipped them yet). Silently
 *     backfilling free-shaped defaults here would misrepresent a real
 *     premium member as free, so this case is `'unavailable'`, not `'loaded'`.
 * - `'loaded'` — a subscription payload is present (including a stale one
 *   kept after a failed background refetch) with at least one of
 *   `archiveAccess`/`issueCadence` genuinely present. A response missing only
 *   ONE of the two keys is an unexpected shape distinct from the
 *   deploy-order case above; today it is backfilled per-key with the
 *   free-shaped default for just that key (`withDigestEntitlementFallbacks`),
 *   same as the legacy `useEntitlements`/`useFeatureAccess` behavior.
 *
 * Because `archiveAccess`/`issueCadence` exist only on the `'loaded'` member
 * of the union, reading them while `'loading'`/`'unavailable'` is a type
 * error, not a convention a reviewer has to catch by eye.
 *
 * **Consumer contract** (what each surface does per state):
 * - `'loading'`: render the surface's skeleton (this project's
 *   skeleton-over-spinner/blank pattern).
 * - `'unavailable'`:
 *   - masthead: show NEITHER the cadence tag NOR the upsell link;
 *   - closing card: render nothing;
 *   - Previous Issues rows: navigable, with no lock glyph — the past-issue
 *     detail route's server 403 -> locked card is the final authority on
 *     access, so being optimistic here is safe and being pessimistic
 *     (locking a paying member out on a flaky network) is not.
 * - `'loaded'`: branch on `archiveAccess`/`issueCadence`.
 *
 * Reads `useGetSubscription()` directly rather than `useEntitlements()`:
 * that hook (and `useFeatureAccess`) intentionally keep their legacy
 * free-defaults behavior for the five existing gates (FR-P10/D1).
 *
 * Assumes the subscription query is always enabled: if it is ever gated on
 * auth, a disabled query with no data reads as `'loading'` forever.
 */
export type DigestEntitlementsResult =
  | { status: 'loading' }
  | { status: 'unavailable' }
  | { status: 'loaded'; archiveAccess: ArchiveAccess; issueCadence: IssueCadence };

export function useDigestEntitlements(): DigestEntitlementsResult {
  const { data: subscription, isError, fetchStatus, errorUpdateCount } = useGetSubscription();

  return useMemo<DigestEntitlementsResult>(() => {
    if (subscription) {
      const raw = subscription.entitlements;
      // Deploy-order case: an API build that predates D51d/D51f's entitlement
      // keys omits BOTH `archiveAccess` and `issueCadence` entirely (as opposed
      // to the "normal loaded" case below, which has them, or an unexpected
      // shape missing only one). The member's real tier is unknown here — a
      // paying member on the old build must never be shown the free-tier
      // upsell (masthead tag/link, closing card, locked strip), so this reads
      // as `unavailable`, not a silent fall-through to free-shaped defaults.
      if (raw?.archiveAccess === undefined && raw?.issueCadence === undefined) {
        return { status: 'unavailable' };
      }
      const entitlements = withDigestEntitlementFallbacks(raw ?? DEFAULT_ENTITLEMENTS);
      return {
        status: 'loaded',
        archiveAccess: entitlements.archiveAccess,
        issueCadence: entitlements.issueCadence,
      };
    }
    // `errorUpdateCount > 0` remembers a settled failure through its own retry:
    // TanStack Query resets an errored, data-less query to `status: 'pending'`
    // with `error: null` the moment a NEW observer mounts (`retryOnMount`'s
    // default), which would otherwise flip every consumer `unavailable` ->
    // `loading` (skeleton) for the whole retry window. Same technique as
    // `deriveDigestIssueStatus`; only a successful fetch (data present) leaves
    // `unavailable`.
    if (isError || fetchStatus === 'paused' || errorUpdateCount > 0) return { status: 'unavailable' };
    return { status: 'loading' };
  }, [subscription, isError, fetchStatus, errorUpdateCount]);
}

/**
 * Hook to check voting mode access.
 *
 * @param mode - The voting mode to check
 * @returns Whether the user can use this voting mode
 */
export function useVotingModeAccess(
  mode: 'simple' | 'survivor' | 'ranking'
): { canUse: boolean; isLoading: boolean } {
  const { data: subscription, isLoading } = useGetSubscription();

  return useMemo(() => {
    const entitlements = subscription?.entitlements ?? DEFAULT_ENTITLEMENTS;

    switch (mode) {
      case 'simple':
        return { canUse: entitlements.simpleVoting, isLoading };
      case 'survivor':
        return { canUse: entitlements.survivorVoting, isLoading };
      case 'ranking':
        return { canUse: entitlements.rankingVoting, isLoading };
      default:
        return { canUse: false, isLoading };
    }
  }, [subscription, mode, isLoading]);
}
